import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import {
    BuildOrderNotificationOptions,
    CustomerNotificationsDoc,
    buildOrderNotification,
    customerNotificationsRef,
    hasNotification
} from '@api/CustomerNotifications/CustomerNotification';
import { Order, OrderStatus, isTableOrder, ordersRef } from '@api/Orders/Order';
import NotificationTemplateServ from '@api/NotificationTemplates/NotificationTemplateServ';

/**
 * Emisor de avisos al CLIENTE desde el admin — lo que mueve el staff.
 *
 * ⚠️ **PARIDAD con krispis-server** (`api/CustomerNotifications/CustomerNotificationServ.ts`),
 * que avisa de lo que mueven Stripe y Uber. Misma colección, misma forma.
 *
 * **Nunca lanza y nunca bloquea.** Cuelga de completar, cancelar y despachar
 * pedidos: el precedente de por qué esto importa está documentado — el `throw`
 * del ticket de WhatsApp se llevó por delante el commit de inventario del 35%
 * de las ventas (2026-08-19).
 *
 * ⚠️ **El push lo manda el SERVER**, no esto: la llave VAPID privada no puede
 * salir de allá. Aquí se guarda el aviso (que es el dato) y se le pide al server
 * que lo empuje, fire-and-forget.
 */

const empujar = (customerId: string, payload: { title: string; body?: string; link?: string; tag?: string }) => {
    // Sin `await` y con su propio catch: el aviso ya está en la campana del
    // cliente. Que además le suene el teléfono es un extra, no un requisito —
    // y el server escala a cero, así que esta llamada puede tardar en frío.
    axiosPrivate
        .post('/push/send-customer', { customerId, ...payload })
        .catch((error) => console.error('No se pudo pedir el push del cliente:', error));
};

export default {
    /**
     * Deja el aviso que le toca a este cambio de estado en la campana del
     * cliente. Devuelve `true` solo si guardó algo nuevo.
     *
     * Cuesta **1 lectura + 1 escritura**: la lectura es para deduplicar por
     * `id`, ya que `arrayUnion` solo deduplica objetos idénticos y cada aviso
     * trae su propio `createdAt`.
     */
    async notifyOrderStatus(
        order: Pick<Order, 'id' | 'status' | 'deliveryType' | 'customer'>,
        status: OrderStatus,
        options: BuildOrderNotificationOptions = {}
    ): Promise<boolean> {
        try {
            const customerId = order?.customer?.uid;
            if (!customerId) return false;

            // Textos editables desde el admin; el resolver cachea 60 s y nunca lanza.
            const overrides = await NotificationTemplateServ.getOverrides();
            const notification = buildOrderNotification(order, status, { ...options, overrides });
            if (!notification) return false;

            const ref = doc(customerNotificationsRef, customerId);
            const snapshot = await getDoc(ref);
            const actual = snapshot.exists() ? (snapshot.data() as CustomerNotificationsDoc) : undefined;
            if (hasNotification(actual?.items, notification.id)) return false;

            await setDoc(
                ref,
                {
                    customerId,
                    // Atómico: dos pedidos del mismo cliente avanzando a la vez
                    // no se pisan, porque no se reescribe el arreglo.
                    items: arrayUnion(notification),
                    updatedAt: Date.now()
                },
                { merge: true }
            );

            empujar(customerId, {
                title: notification.title,
                body: notification.body,
                link: notification.link,
                tag: notification.orderId || notification.id
            });

            return true;
        } catch (error) {
            console.error('No se pudo crear la notificación del cliente:', error);
            return false;
        }
    },

    /**
     * Igual que el anterior, cuando solo se tiene el id — el caso de
     * `OrderServ.updateStatus`, que es por donde pasan TODAS las acciones del
     * tablero de cocina.
     *
     * ⚠️ Lee la orden (`ordersRef` directo, no `OrderServ`, para no crear un
     * ciclo de imports) y de ahí saca el cliente, el tipo de entrega y si es de
     * mesa. Esa última se resuelve con **`isTableOrder`** y nunca mirando
     * `order.tableKiosk`: desde que el kiosco guarda el suyo, tener `tableKiosk`
     * ya no significa "es una mesa".
     */
    async notifyOrderStatusById(orderId: string, status: OrderStatus): Promise<boolean> {
        try {
            const snapshot = await getDoc(doc(ordersRef, orderId));
            if (!snapshot.exists()) return false;
            const order = { ...(snapshot.data() as Order), id: orderId };
            return await this.notifyOrderStatus(order, status, { esMesa: isTableOrder(order) });
        } catch (error) {
            console.error('No se pudo leer el pedido para notificar al cliente:', error);
            return false;
        }
    }
};
